import type {
  QuestionCustomizationsQuestion,
  QuestionUsage,
  QuestionUsageSource,
} from '../../services/questionCustomizations';
import { orderQuestionUsageSources } from '../../services/questionCustomizations';

export type QuestionCatalogGroup = {
  key: string;
  label: string;
  description: string;
  questions: QuestionCustomizationsQuestion[];
};

function groupForUsage(
  sources: readonly QuestionUsageSource[],
  hasMetadataFailure: boolean
): Omit<QuestionCatalogGroup, 'questions'> | null {
  const orderedSources = orderQuestionUsageSources(sources);
  const primary = orderedSources[0];
  if (!primary) {
    // This is a truthful loading failure state, not a catchall taxonomy. It
    // keeps the complete catalog usable while form metadata is unavailable.
    return hasMetadataFailure
      ? {
          key: 'usage-metadata-failure',
          label: 'Question groups unavailable',
          description: 'Showing all questions while visit form details load.',
        }
      : null;
  }

  return {
    key: primary.taxonomyKey,
    label: primary.taxonomyLabel,
    description: primary.taxonomyDescription,
  };
}

/**
 * Put each catalog QID in exactly one supported documentation taxonomy.
 * Shared questions keep every real form context on the row, so the add control
 * remains unique and the primary group stays deterministic.
 */
export function groupQuestionCatalog(
  questions: QuestionCustomizationsQuestion[],
  usage: QuestionUsage,
  hasMetadataFailure = false
): QuestionCatalogGroup[] {
  const groups = new Map<string, QuestionCatalogGroup>();
  const seenQids = new Set<string>();

  questions.forEach(question => {
    if (seenQids.has(question.qid)) return;
    seenQids.add(question.qid);
    const group = groupForUsage(
      usage.get(question.qid) ?? [],
      hasMetadataFailure
    );
    if (!group) return;
    const current = groups.get(group.key);
    if (current) {
      current.questions.push(question);
    } else {
      groups.set(group.key, { ...group, questions: [question] });
    }
  });

  return [...groups.values()];
}
